import React from 'react';
import { CalendarCheck, Clock, MessageCircle, ShieldCheck, Phone, Video } from 'lucide-react';
import { PORTAL_URL, PHONE_DISPLAY, PHONE_TEL } from '../SiteLayout';
import PageSEO from '../../components/PageSEO';

const WHY_BOOK = [
  'Réduire votre temps d\u2019attente',
  'Garantir la disponibilité du professionnel souhaité',
  'Préparer votre dossier médical avant votre arrivée',
  'Améliorer l\u2019organisation de votre parcours de soins',
  'Recevoir automatiquement des rappels afin de ne pas oublier votre rendez-vous',
];

const STEPS = [
  { n: '1', text: 'Choisissez la spécialité ou le médecin que vous souhaitez consulter.' },
  { n: '2', text: 'Sélectionnez la date et l\u2019heure disponibles.' },
  { n: '3', text: 'Renseignez vos informations personnelles.' },
  { n: '4', text: 'Recevez la confirmation de votre rendez-vous par WhatsApp.' },
  { n: '5', text: 'Présentez-vous le jour convenu ou connectez-vous à votre téléconsultation.' },
];

const FAQS = [
  { q: 'Puis-je choisir mon médecin ?', a: 'Oui, selon les disponibilités de chaque praticien.' },
  { q: 'Puis-je prendre rendez-vous pour un proche ?', a: 'Oui. Vous pouvez effectuer une réservation pour un membre de votre famille.' },
  { q: 'Dois-je payer avant la consultation ?', a: "Selon le type de consultation, un paiement en ligne pourra être proposé. Les autres modalités vous seront précisées lors de la réservation." },
  { q: 'Vais-je recevoir un rappel ?', a: 'Oui. Des rappels automatiques seront envoyés avant votre rendez-vous.' },
  { q: 'Puis-je transformer un rendez-vous présentiel en téléconsultation ?', a: "Lorsque votre situation médicale le permet, cette possibilité pourra vous être proposée après validation par l'équipe médicale." },
];

export default function SiteRdv() {
  return (
    <main data-testid="site-rdv">
      <PageSEO
        title="Prendre rendez-vous en ligne — Matadi & Boma"
        description="Prenez rendez-vous en ligne 24h/24 avec un médecin du Centre Médical la Trinité à Matadi ou Boma. Consultation présentielle ou téléconsultation, paiement Mobile Money."
        path="/rendez-vous"
      />
      <div className="site-page-head">
        <div className="site-page-head-inner">
          <h1 className="site-reveal">Prenez rendez-vous en toute simplicité</h1>
          <p className="site-reveal-1">
            Choisissez votre spécialité, sélectionnez un créneau disponible et recevez la confirmation
            de votre rendez-vous en quelques instants. Notre équipe reste également disponible pour
            vous accompagner par téléphone ou via WhatsApp.
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-primary" data-testid="rdv-cta-book-btn">
              <CalendarCheck size={18} /> Prendre rendez-vous
            </a>
            <a href="/teleconsultation" className="site-btn site-btn-outline" style={{ background: 'white' }} data-testid="rdv-cta-tele-btn">
              <Video size={18} /> Téléconsultation
            </a>
          </div>
        </div>
      </div>

      {/* Pourquoi */}
      <section className="site-section">
        <span className="site-kicker">Pourquoi prendre rendez-vous ?</span>
        <h2>Pourquoi réserver votre consultation à l&apos;avance ?</h2>
        <ul style={{ marginTop: '1.5rem', columnCount: 2, columnGap: '2.5rem', listStyle: 'none', padding: 0 }}>
          {WHY_BOOK.map((w, i) => (
            <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: '.85rem', breakInside: 'avoid' }}>
              <ShieldCheck size={18} color="var(--site-primary)" style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{w}</span>
            </li>
          ))}
        </ul>
        <p style={{ marginTop: '1rem', color: 'var(--site-muted)' }}>
          Notre objectif est de rendre votre expérience plus simple, plus rapide et plus confortable.
        </p>
      </section>

      {/* Étapes */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Comment prendre rendez-vous ?</span>
          <h2>Réservez en quelques étapes</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginTop: '2rem' }}>
            {STEPS.map((s) => (
              <div key={s.n} className="site-card" style={{ textAlign: 'left', borderTop: '4px solid var(--site-primary)' }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--site-primary)' }}>{s.n}</div>
                <p style={{ marginTop: 6, fontSize: 14 }}>{s.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Après */}
      <section className="site-section">
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <span className="site-kicker">Après votre réservation</span>
          <h2>Que se passe-t-il ensuite ?</h2>
          <ul style={{ marginTop: '1rem', paddingLeft: '1.5rem', color: 'var(--site-muted)', lineHeight: 1.75 }}>
            <li>Vous recevez immédiatement une confirmation.</li>
            <li>Un rappel vous sera envoyé avant votre consultation.</li>
            <li>Si des documents sont nécessaires, vous serez informé à l&apos;avance.</li>
            <li>Votre dossier patient sera préparé afin de faciliter votre accueil.</li>
          </ul>
        </div>
      </section>

      {/* Modifier / annuler */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <span className="site-kicker">Modifier ou annuler</span>
            <h2>Un imprévu ?</h2>
            <p style={{ color: 'var(--site-muted)', lineHeight: 1.75 }}>
              Si vous ne pouvez pas vous présenter au rendez-vous prévu, nous vous invitons à modifier
              ou annuler votre réservation dès que possible. Cette démarche permet de proposer le créneau
              à un autre patient. La modification pourra être effectuée depuis votre espace patient, par
              téléphone ou via WhatsApp.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: '1rem', flexWrap: 'wrap' }}>
              <a href={`tel:${PHONE_TEL}`} className="site-btn site-btn-outline"><Phone size={16} /> {PHONE_DISPLAY}</a>
              <a href={`https://wa.me/${PHONE_TEL.replace('+', '')}`} target="_blank" rel="noreferrer" className="site-btn site-btn-outline"><MessageCircle size={16} /> WhatsApp</a>
            </div>
          </div>
        </section>
      </div>

      {/* FAQ */}
      <section className="site-section">
        <span className="site-kicker">Questions fréquentes</span>
        <h2>Vos questions, nos réponses</h2>
        <div style={{ marginTop: '1.5rem', display: 'grid', gap: 10 }}>
          {FAQS.map((f, i) => (
            <details key={i} style={{ padding: '1rem 1.25rem', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{f.q}</summary>
              <p style={{ marginTop: '.5rem', color: 'var(--site-muted)' }}>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="site-section">
        <div className="site-cta-band">
          <div>
            <h2>Votre santé mérite toute notre attention</h2>
            <p>Réservez votre consultation dès aujourd&apos;hui et bénéficiez d&apos;une prise en charge personnalisée.</p>
          </div>
          <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-outline" style={{ background: 'white' }}>
            <CalendarCheck size={18} /> Prendre rendez-vous
          </a>
        </div>
      </section>
    </main>
  );
}
